import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAbility } from '@casl/react';
import { ArrowLeft, FileText, Loader2, Paperclip, Plus, Sparkles, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Combobox } from '../../components/ui/combobox';
import AccessRestricted from '../../components/AccessRestricted';
import { AbilityContext } from '../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../constants/permissions.constants';
import { Vendor } from '../../types/lists.types';
import { AiStatus, Product, Yard } from '../../types/stock.types';
import { vendorsApi } from '../../utils/lists-api';
import { importsApi, productsApi, stockAiApi, yardsApi } from '../../utils/stock-api';
import GenerateOrderDialog, { GeneratedLine } from './components/GenerateOrderDialog';
import SheetFilePicker from './components/SheetFilePicker';

interface DraftLine { productId: string; originalQuantity: string }

const today = () => new Date().toISOString().slice(0, 10);

/** Matches the documents endpoint's per-request limit. */
const MAX_SOURCE_DOCS = 10;

/**
 * New Import Order (STOCK.md §6.1).
 *
 * A full page rather than a dialog, matching every other create/edit screen in Idarah:
 * the order-in-progress gets its own URL, so it can be linked, reloaded and backed
 * out of — and the AI sheet-upload + line review has room to breathe.
 *
 * Order lines carry the ORIGINAL quantity ordered — the figure every receipt is later
 * balanced against (§6.3). An import order without lines has nothing to balance, so at
 * least one is required up front, unlike a Return where the count starts empty.
 *
 * Vendors are filtered to those with "Incoming Stock" ticked in Show in Modules, per
 * §6.1 and LISTS.md — a uniform supplier has no business in a container manifest.
 *
 * Lines can be filled two ways, and mixed: picked by hand in the rows below, or
 * generated from a pasted list / attached sheet photo(s)/PDF via "Generate Order"
 * (§2.5), which opens the shared parse-and-review flow and merges the reviewed lines
 * into the same rows. The CTA is hidden rather than disabled when the service is
 * down — a button that cannot work is worse than no button — and the §2.6
 * manual-entry guidance shows instead.
 */
export default function ImportOrderFormPage() {
    const navigate = useNavigate();
    const ability = useAbility(AbilityContext);
    const canCreate = ability.can(ACTIONS.CREATE, RESOURCES.STOCK_IMPORT_ORDER);
    const { yardId: yardIdParam } = useParams<{ yardId?: string }>();
    const scopedYardId = yardIdParam ? Number(yardIdParam) : null;

    const [yardId, setYardId] = useState<string>(scopedYardId ? String(scopedYardId) : '');
    const [vendorId, setVendorId] = useState<string>('');
    const [expectedDate, setExpectedDate] = useState(today());
    const [lines, setLines] = useState<DraftLine[]>([{ productId: '', originalQuantity: '' }]);
    const [yards, setYards] = useState<Yard[]>([]);
    const [vendors, setVendors] = useState<Vendor[]>([]);
    const [products, setProducts] = useState<Product[]>([]);
    const [saving, setSaving] = useState(false);
    const [aiOpen, setAiOpen] = useState(false);
    const [aiStatus, setAiStatus] = useState<AiStatus | null>(null);
    /** The paste-first list area — handed to Generate Order (§2.5), cleared once added. */
    const [aiText, setAiText] = useState('');
    /** Sheet photo(s)/PDF attached here, parsed by Generate Order, cleared once added. */
    const [aiFiles, setAiFiles] = useState<File[]>([]);
    /** Kept so it can be stored on the order for audit (§2.5). */
    const [rawInputText, setRawInputText] = useState<string | null>(null);
    /** ai_usage_logs row for the parse, sent on create to attribute the spend. */
    const [aiUsageId, setAiUsageId] = useState<number | null>(null);
    /** The sheet file(s) the AI read — stored with the order as evidence (§12.4). */
    const [sheetFiles, setSheetFiles] = useState<File[]>([]);
    /** Source paperwork attached by hand — the storage fallback (§12.4). */
    const [docFiles, setDocFiles] = useState<File[]>([]);
    const docInput = useRef<HTMLInputElement | null>(null);

    // Creating into a closed yard is not a thing: the options are open yards only, and
    // a scoped yard that isn't open simply doesn't narrow them.
    const scopedYard = scopedYardId ? yards.find((y) => y.id === scopedYardId) ?? null : null;
    const yardOptions = scopedYard ? [scopedYard] : yards;
    const vendorOptions = vendors.map((v) => ({ label: v.tradingName, value: String(v.id) }));
    const productOptions = products.map((p) => ({ label: `${p.name} (${p.sku})`, value: String(p.id) }));

    const listPath = scopedYardId ? `/stock/yards/${scopedYardId}/imports` : '/stock/imports';
    const detailPath = (id: number) =>
        scopedYardId ? `/stock/yards/${scopedYardId}/imports/${id}` : `/stock/imports/${id}`;

    useEffect(() => {
        if (!canCreate) return;
        yardsApi.list({ status: 'open' }).then((list) => {
            setYards(list);
            // A scoped yard that isn't open (e.g. closed) can't take new orders —
            // don't keep it preselected.
            if (scopedYardId && !list.some((y) => y.id === scopedYardId)) setYardId('');
        }).catch(() => undefined);
        vendorsApi.list({ status: 'active', module: 'Incoming Stock' })
            .then(setVendors).catch(() => undefined);
        productsApi.list({ status: 'active' }).then(setProducts).catch(() => undefined);
        // §2.6 — decides whether the CTA or the manual-entry microcopy is shown.
        stockAiApi.status().then(setAiStatus).catch(() =>
            setAiStatus({ available: false, model: '', message: null }));
    }, [canCreate, scopedYardId]);

    /**
     * Merges reviewed AI lines into the draft. Appends rather than replaces — the user
     * may have already started some rows by hand, and silently discarding in-progress
     * work would be rude. The sheet files ride along too — they are stored with the
     * order as evidence of what the AI read (§12.4).
     */
    const handleGenerated = (
        generated: GeneratedLine[], raw: string, files: File[], usageId: number | null,
    ) => {
        setRawInputText(raw);
        setAiUsageId(usageId);
        setSheetFiles((prev) => {
            const seen = new Set(prev.map((f) => `${f.name}:${f.size}`));
            return [...prev, ...files.filter((f) => !seen.has(`${f.name}:${f.size}`))];
        });
        // The input is now lines in the draft — clear the paste area and the sheets so
        // a second go starts fresh instead of silently re-reading the same input.
        setAiText('');
        setAiFiles([]);
        setLines((prev) => {
            const typed = prev.filter((l) => l.productId || l.originalQuantity);
            const merged = [...typed];
            for (const g of generated) {
                const existing = merged.findIndex((l) => l.productId === String(g.productId));
                if (existing >= 0) {
                    // Same product from both sources — add the quantities rather than
                    // dropping one silently.
                    merged[existing] = {
                        ...merged[existing],
                        originalQuantity: String(Number(merged[existing].originalQuantity) + g.quantity),
                    };
                } else {
                    merged.push({ productId: String(g.productId), originalQuantity: String(g.quantity) });
                }
            }
            return merged.length ? merged : [{ productId: '', originalQuantity: '' }];
        });
        toast.success(`${generated.length} line(s) added — check them before creating.`);
    };

    const setLine = (index: number, patch: Partial<DraftLine>) =>
        setLines((prev) => prev.map((l, i) => (i === index ? { ...l, ...patch } : l)));

    const handleDocFiles = (picked: FileList | null) => {
        if (!picked?.length) return;
        const incoming = Array.from(picked);
        if (docFiles.length + incoming.length > MAX_SOURCE_DOCS) {
            toast.error(`Up to ${MAX_SOURCE_DOCS} documents — the extra one(s) were ignored.`);
        }
        // Original bytes, no compression — this is the evidentiary copy (§12.4).
        setDocFiles((prev) => [...prev, ...incoming].slice(0, MAX_SOURCE_DOCS));
    };

    const validLines = lines
        .filter((l) => l.productId && Number(l.originalQuantity) > 0)
        .map((l) => ({ productId: Number(l.productId), originalQuantity: Number(l.originalQuantity) }));

    // §2.6 — null means the status probe hasn't answered yet; treat that as available
    // so the AI box doesn't flicker in and out while it loads.
    const aiAvailable = aiStatus === null || aiStatus.available;

    const canSubmit = Boolean(yardId && vendorId && expectedDate && validLines.length > 0) && !saving;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!canSubmit) return;
        try {
            setSaving(true);
            const order = await importsApi.create({
                yardId: Number(yardId),
                vendorId: Number(vendorId),
                expectedDate,
                lines: validLines,
                // Only set when the list came through the parser — §2.5 wants the raw
                // text kept alongside what was made of it, and the ledger row linked
                // to the order it produced.
                rawInputText,
                aiUsageId,
            });
            // §12.4 — the order has to exist before documents can hang off it, so the
            // paperwork uploads right after the create. A failure here must not lose
            // the order: warn and carry on; the detail page can take them later.
            try {
                if (sheetFiles.length) await importsApi.uploadDocuments(order.id, sheetFiles, 'ai_parse');
                if (docFiles.length) await importsApi.uploadDocuments(order.id, docFiles, 'upload');
            } catch {
                toast.error(
                    `${order.orderNumber} was created, but the document(s) failed to upload ` +
                    `— attach them again from the order page.`
                );
            }
            toast.success(`${order.orderNumber} created`);
            navigate(detailPath(order.id));
        } catch (err: any) {
            toast.error(err?.data?.message || err?.message || 'Failed to create import order');
            setSaving(false);
        }
    };

    if (!canCreate) return <AccessRestricted />;

    return (
        <div className="space-y-4 md:space-y-6 p-2 md:p-0 max-w-3xl">
            {/* Header */}
            <div>
                <Button variant="ghost" size="sm" onClick={() => navigate(listPath)}
                        className="mb-2 -ml-2">
                    <ArrowLeft size={16} className="mr-1.5" />Import Orders
                </Button>
                <h1 className="text-2xl font-bold">New Import Order</h1>
                <p className="text-sm text-muted-foreground mt-1">
                    What you've ordered from a vendor. Containers are recorded against it as
                    receipts when they arrive.
                </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
                <Card>
                    <CardContent className="p-4 md:p-6 space-y-5">
                        <h2 className="text-base font-semibold">Details</h2>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="import-yard">Yard *</Label>
                                <Select value={yardId} onValueChange={setYardId}>
                                    <SelectTrigger id="import-yard">
                                        <SelectValue placeholder={
                                            yardOptions.length ? 'Select a yard' : 'No open yards'
                                        } />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {yardOptions.map((y) => (
                                            <SelectItem key={y.id} value={String(y.id)}>
                                                {y.yardName} ({y.yardCode})
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label htmlFor="import-date">Expected Date *</Label>
                                <Input
                                    id="import-date"
                                    type="date"
                                    value={expectedDate}
                                    onChange={(e) => setExpectedDate(e.target.value)}
                                />
                            </div>
                        </div>

                        <div>
                            <Label>Vendor *</Label>
                            <Combobox
                                options={vendorOptions}
                                value={vendorId || undefined}
                                onChange={(v) => setVendorId(v || '')}
                                placeholder={vendors.length ? 'Select a vendor' : 'No vendors set to Incoming Stock'}
                                searchPlaceholder="Search vendors..."
                                emptyMessage="No vendors found."
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                                Only vendors with "Incoming Stock" ticked under Show in Modules appear
                                here. Set that under Lists → Vendors.
                            </p>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="p-4 md:p-6 space-y-4">
                        <div>
                            <h2 className="text-base font-semibold">Order lines *</h2>
                            <p className="text-xs text-muted-foreground mt-1">
                                The original quantity ordered. Receipts are balanced against
                                these figures.
                            </p>
                        </div>

                        {/* §2.5 — AI assist, one way to fill the rows: paste the list or
                            attach sheet photo(s)/a PDF here, Generate turns them into
                            draft rows below. Hidden rather than disabled when the service
                            is down (§2.6). */}
                        {aiAvailable && (
                            <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-3 space-y-2">
                                <p className="text-xs font-medium text-blue-900">
                                    Paste the list as it was written, or attach photo(s)/a PDF
                                    of the sheet — slang and shorthand are fine.
                                </p>
                                <Textarea
                                    value={aiText}
                                    onChange={(e) => setAiText(e.target.value)}
                                    rows={4}
                                    placeholder={'40 standards 3.0m\n20 8ft ledgers\na dozen 2.4m boards\n15 swivels'}
                                    className="font-mono text-sm bg-white"
                                />
                                <SheetFilePicker files={aiFiles} onChange={setAiFiles} />
                                <div className="flex items-center justify-between gap-2">
                                    <p className="text-xs text-muted-foreground">
                                        AI turns it into lines you review before they're added.
                                    </p>
                                    <Button type="button" size="sm" onClick={() => setAiOpen(true)}
                                            disabled={!aiText.trim() && aiFiles.length === 0}>
                                        <Sparkles size={14} className="mr-1.5" />Generate Order
                                    </Button>
                                </div>
                            </div>
                        )}
                        {aiStatus !== null && !aiStatus.available && (
                            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                                Generate with AI is currently down — add the products and
                                quantities manually below.
                            </p>
                        )}

                        {/* Manual entry — always available, alone or mixed with AI lines. */}
                        {aiAvailable && (
                            <p className="text-xs text-muted-foreground">
                                Or add the lines by hand:
                            </p>
                        )}
                        <div className="space-y-2">
                            {lines.map((line, index) => (
                                <div key={index} className="grid grid-cols-[2fr_1fr_auto] gap-2">
                                    <Combobox
                                        options={productOptions}
                                        value={line.productId || undefined}
                                        onChange={(v) => setLine(index, { productId: v || '' })}
                                        placeholder="Select a product"
                                        searchPlaceholder="Search products..."
                                        emptyMessage="No products found."
                                    />
                                    <Input
                                        type="number"
                                        inputMode="numeric"
                                        min="1"
                                        placeholder="Qty"
                                        value={line.originalQuantity}
                                        onChange={(e) => setLine(index, { originalQuantity: e.target.value })}
                                    />
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        aria-label="Remove line"
                                        disabled={lines.length === 1}
                                        onClick={() => setLines((prev) => prev.filter((_, i) => i !== index))}
                                    >
                                        <X size={16} />
                                    </Button>
                                </div>
                            ))}
                        </div>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setLines((prev) => [...prev, { productId: '', originalQuantity: '' }])}
                        >
                            <Plus size={14} className="mr-1.5" />Add line
                        </Button>
                    </CardContent>
                </Card>

                {/* §12.4 — source paperwork: the storage fallback, plus the sheet the AI
                    read (tagged). Stored with the order as the source of record; the
                    original bytes go up, no compression. */}
                <Card>
                    <CardContent className="p-4 md:p-6 space-y-4">
                        <div>
                            <h2 className="text-base font-semibold">
                                Source documents <span className="font-normal text-muted-foreground">(optional)</span>
                            </h2>
                            <p className="text-xs text-muted-foreground mt-1">
                                Attach the paperwork behind this order — the supplier invoice, booking
                                confirmation, PDFs or photos — so the original is never lost.
                            </p>
                        </div>
                        <input
                            ref={docInput}
                            type="file"
                            accept="application/pdf,image/*"
                            multiple
                            className="hidden"
                            onChange={(e) => {
                                handleDocFiles(e.target.files);
                                // Reset so picking the same file again still fires onChange.
                                e.target.value = '';
                            }}
                        />
                        <Button type="button" variant="outline" size="sm"
                                onClick={() => docInput.current?.click()}>
                            <Paperclip size={14} className="mr-1.5" />Attach documents
                        </Button>
                        {(sheetFiles.length > 0 || docFiles.length > 0) && (
                            <div className="divide-y divide-border border border-border rounded-lg bg-white">
                                {([
                                    ...sheetFiles.map((f, i) => ({
                                        file: f, tag: 'AI sheet', key: `sheet-${i}`,
                                        remove: () => setSheetFiles((prev) =>
                                            prev.filter((_, j) => j !== i)),
                                    })),
                                    ...docFiles.map((f, i) => ({
                                        file: f, tag: null as string | null, key: `doc-${i}`,
                                        remove: () => setDocFiles((prev) =>
                                            prev.filter((_, j) => j !== i)),
                                    })),
                                ]).map(({ file, tag, key, remove }) => (
                                    <div key={key} className="flex items-center gap-3 p-2">
                                        <span className="w-10 h-10 rounded-md border border-border bg-muted/40 flex items-center justify-center shrink-0">
                                            <FileText size={16} className="text-muted-foreground" />
                                        </span>
                                        <span className="text-sm truncate flex-1">{file.name}</span>
                                        {tag && (
                                            <span className="text-[10px] font-medium text-blue-700 bg-blue-50 rounded px-1.5 py-0.5 shrink-0">
                                                {tag}
                                            </span>
                                        )}
                                        <Button type="button" variant="ghost" size="icon"
                                                aria-label="Remove document" onClick={remove}>
                                            <X size={16} />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>

                <div className="flex flex-col-reverse md:flex-row md:justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => navigate(listPath)}
                            className="w-full md:w-auto">
                        Cancel
                    </Button>
                    <Button type="submit" disabled={!canSubmit} className="w-full md:w-auto">
                        {saving && <Loader2 size={16} className="animate-spin mr-2" />}
                        Create Import Order
                    </Button>
                </div>
            </form>

            {/* Outside the form — its review-step buttons must not submit the order. */}
            <GenerateOrderDialog
                open={aiOpen}
                onOpenChange={setAiOpen}
                products={products}
                onConfirm={handleGenerated}
                initialText={aiText}
                initialFiles={aiFiles}
            />
        </div>
    );
}
